import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { LoginService } from '../login/login.service';

const PENDING_ORG_KEY = 'translator-selected-org:pending';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  constructor(private readonly loginService: LoginService) {
    this._captureOrgFromUrl();
  }

  get allowedOrgIds(): string[] {
    const ids = new Set<string>();
    if (environment.csteamOrganizationId) {
      ids.add(environment.csteamOrganizationId);
    }
    if (environment.icteamOrganizationId) {
      ids.add(environment.icteamOrganizationId);
    }
    return [...ids];
  }

  // Org for the organizationid header; '' when unknown (dual-org, ambiguous).
  getOrganizationId(): string {
    if (!this.loginService.hasInfo) {
      const pending = localStorage.getItem(PENDING_ORG_KEY)?.trim() ?? '';
      return pending && this.allowedOrgIds.includes(pending) ? pending : '';
    }
    const memberOrgIds = this._memberOrgIds();
    return memberOrgIds.length === 1 ? memberOrgIds[0] : environment.csteamOrganizationId;
  }

  selectOrganization(organizationId: string): void {
    if (!this.allowedOrgIds.includes(organizationId)) {
      throw new Error(`Organization ${organizationId} is not served by this translator`);
    }
    if (this.loginService.hasInfo && !this._isUserMemberOfOrg(organizationId)) {
      throw new Error(`Organization ${organizationId} is not available for this user`);
    }
    localStorage.setItem(PENDING_ORG_KEY, organizationId);
    if (this.loginService.hasInfo) {
      localStorage.setItem(this._storageKey(), organizationId);
    }
  }

  private _captureOrgFromUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const org = new URLSearchParams(window.location.search).get('org')?.trim();
    if (org && this.allowedOrgIds.includes(org)) {
      this.selectOrganization(org);
    }
  }

  private _storageKey(): string {
    return `translator-selected-org:${this.loginService.userInfo.userName}`;
  }

  private _memberOrgIds(): string[] {
    return (this.loginService.userInfo?.organizationIds ?? []).filter((id) =>
      this.allowedOrgIds.includes(id),
    );
  }

  private _isUserMemberOfOrg(organizationId: string): boolean {
    if (!this.allowedOrgIds.includes(organizationId)) {
      return false;
    }
    if (!this.loginService.hasInfo) {
      return true;
    }
    return (this.loginService.userInfo.organizationIds ?? []).includes(organizationId);
  }
}
